import { Button, Form, Input, message, Radio } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { secondaryColor, Types } from "../../../config";
import { setData } from "../../../redux/actions";
import { store } from "../../../redux/store";
import { isAuthenticated } from "../../../utils/auth";
import { getPostLoginRoute } from "../../../utils/userRoutes";
import { translateAuthApiMessage } from "../auth-i18n";

const USER_TYPE_ADMIN = "admin";
const USER_TYPE_STAFF = "staff";

const inputFieldStyle = {
  borderRadius: 10,
  fontSize: "14px",
  height: "60px",
};

export default function SignIn() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const languageDirection = useSelector((state) => state.language?.direction);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();
  const userType = Form.useWatch("user_type", form) || USER_TYPE_ADMIN;
  const isStaffLogin = userType === USER_TYPE_STAFF;
  const bgImageSrc =
    languageDirection === "rtl"
      ? "../../assets/bew-bg-ar.png"
      : "../../assets/bew-bg.png";

  const handleLogin = async (data) => {
    const type = Types?.[data?.data?.user_data?.user_type];
    const token = data?.data?.auth_token;
    let permissions = data?.data?.user_data?.permissions || [];

    const obj = {
      user: {
        ...data?.data,
        user_data: {
          ...data?.data?.user_data,
          permissions,
        },
      },
      loginType: type,
      token,
      seller_type: "seller",
      permissions,
      sellerAccountIds: data?.data?.user_data?.seller_account_ids || [],
      auth_type: data?.data?.user_data?.auth_type,
      tenant_user_id: data?.data?.user_data?.tenant_user_id,
    };

    dispatch(setData(obj));

    navigate(getPostLoginRoute(type, store.getState()?.user?.data || obj));
  };

  useEffect(() => {
    const storedMessage = sessionStorage.getItem("login_message");
    if (storedMessage) {
      message.warning(translateAuthApiMessage(storedMessage, t));
      sessionStorage.removeItem("login_message");
    }

    if (isAuthenticated(user)) {
      navigate(getPostLoginRoute(user?.loginType, user), { replace: true });
    }
    setLoading(false);
  }, [user, navigate]);

  const onFinish = async (values) => {
    setSubmitLoading(true);
    const payload = {
      username: values.username,
      password: values.password,
    };
    if (values.user_type === USER_TYPE_STAFF) {
      payload.business_id = values.business_id?.trim();
    }
    const response = await MakeApiCall("login", "post", payload);
    if (response?.status === true) {
      console.log("response", response);
      await handleLogin(response);
      setSubmitLoading(false);
    } else {
      setSubmitLoading(false);
      message.destroy();
      message.error(translateAuthApiMessage(response?.message, t));
    }
  };

  return (
    <Wrapper>
      <div className="login-page">
        <div className="form-section">
          <div className="login-card fadeInDown">
            <div className="login-card-inner">
              <div className="login-logo">
                <img alt="Logo" src="../../assets/logo-icon.png" />
              </div>
              <Form
                form={form}
                name="signin"
                className="login-form"
                initialValues={{ remember: true, user_type: USER_TYPE_ADMIN }}
                onFinish={onFinish}
              >
                <div className="login-form-body">
                  <div className="login-heading">
                    <h1>{t("signIn.title", "Sign In to your account")}</h1>
                    <div className="login-subtitle">
                      {t(
                        "signIn.subtitle",
                        "Enter your credentials to view all insights"
                      )}
                    </div>
                  </div>
                  <Form.Item name="user_type" style={{ marginBottom: 8 }}>
                    <Radio.Group
                      onChange={() =>
                        form.setFieldValue("business_id", undefined)
                      }
                    >
                      <Radio value={USER_TYPE_ADMIN}>
                        {t("signIn.admin", "Admin")}
                      </Radio>
                      <Radio
                        value={USER_TYPE_STAFF}
                        style={{ marginInlineStart: 24 }}
                      >
                        {t("signIn.staff", "Staff")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {isStaffLogin && (
                    <Form.Item
                      name="business_id"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "signIn.businessIdRequired",
                            "Please enter your Business ID!"
                          ),
                        },
                      ]}
                    >
                      <Input
                        style={inputFieldStyle}
                        size="large"
                        placeholder={t("signIn.businessId", "Business ID")}
                      />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="username"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "signIn.emailPhoneRequired",
                          "Please enter your email or username!"
                        ),
                      },
                    ]}
                  >
                    <Input
                      style={inputFieldStyle}
                      size="large"
                      placeholder={t(
                        "signIn.emailOrUsername",
                        "Email or username"
                      )}
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "signIn.passwordRequired",
                          "Please input your password!"
                        ),
                      },
                    ]}
                  >
                    <Input.Password
                      style={inputFieldStyle}
                      size="large"
                      placeholder={t("signIn.password", "Password")}
                    />
                  </Form.Item>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      alignItems: "center",
                      fontSize: "16px",
                      marginBottom: "0",
                    }}
                  >
                    <div />
                    <Link
                      to="/seller-reset-password"
                      style={{
                        color: "#212529",
                        textDecoration: "none",
                        marginInlineStart: "auto",
                      }}
                      className="head-subtitle"
                    >
                      {t("signIn.forgotPassword", "Forgot Password?")}
                    </Link>
                  </div>
                  <div>
                    <Form.Item>
                      <Button
                        style={{
                          marginTop: "10px",
                          backgroundColor: secondaryColor,
                          color: "#FFF",
                          fontSize: "15px",
                          border: "none",
                          height: "50px",
                        }}
                        loading={submitLoading}
                        size="large"
                        htmlType="submit"
                        block
                      >
                        {t("signIn.signInButton", "Sign In")}
                      </Button>
                    </Form.Item>
                  </div>
                </div>
                <div className="login-footer-links">
                  <span>{t("signIn.notMember", "Not a Member yet?")}</span>
                  <Link to="/sign-up">{t("signIn.signUp", "Sign Up")}</Link>
                </div>
              </Form>
            </div>
          </div>
          <div className="login-footer-bar">
            <div className="login-copyright">
              {t("signIn.copyrightPrefix", "Copyright @")}{" "}
              <strong>{t("signIn.copyrightBrand", "Loops")}</strong>{" "}
              {t("signIn.copyrightSuffix", "2026. All rights reserved.")}
            </div>
          </div>
        </div>
        <div className="bg-section">
          <img src={bgImageSrc} alt="" className="bg-image" />
        </div>
        <div className="language-switcher">
          <LanguageSwitcher placement="top" />
        </div>
      </div>
    </Wrapper>
  );
}
